import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { Card, Grid } from '@mui/material'

import {
  EOrderStatus,
  IOrder,
  OrderSatausLabel,
  OrderSummary,
} from '../../../../types/order'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import useAxios, { axiosInstance } from '../../../../hooks/useAxios'
import PageUrls from '../../../../constants/page-urls'
import OrderListTeleSaleTable from './components/OrderListTeleSaleTable'
import { OrderMenuSelect } from '../../components/OrderMenuSelect'
import OrderFilter, { IFilterMapped } from '../../components/OrderFilter'
import { message } from 'antd'
import { IImportByOrdersForm } from '../../components/OrderImportLeadFormModal'
import { UserRole } from '../../../../types/user'
import { useUser } from '../../../../hooks/useUser'
import dayjs, { Dayjs } from 'dayjs'
import { MAX_DONWLOAD_PAGE_SIZE } from '../../../../constants/global'
import * as XLSX from 'xlsx'
import { LeadExcelStatus } from '../OrderList'
import { ERRORS_MESSAGE_MAPPER } from '../../../../constants/errors'
import { SHIPPING_PROVIDER_TEXT } from '../../../../constants/shipping-status'

const Menus: any[] = []

const OrderListTeleSalePage = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)
  const { user, merchant } = useUser()

  const { c: _c, ch: _ch, ...queryStrWithoutCursor } = queryStr
  const initialFilter: IFilterMapped = {
    page: Number(queryStr.page ?? 1),
    perPage: Number(queryStr.perPage ?? 15),
    ...(queryStrWithoutCursor as IFilterMapped),
  }
  const [searchFilter, setSearchFilter] = useState<IFilterMapped>(initialFilter)
  const [filter, setFilter] = useState<IFilterMapped>(initialFilter)
  const [cursor, setCursor] = useState<{
    next?: string
    prev?: string
  }>({
    next: (queryStr.c as string) || undefined,
  })
  const [cursorHistory, setCursorHistory] = useState<string[]>(
    (queryStr.ch as string)
      ? ((queryStr.ch as string).split(',').filter(Boolean) as string[])
      : [],
  )

  const [selectedOrders, setSelectedOrders] = useState<IOrder[]>([])

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const navigateWithCursor = (
    nextFilter: IFilterMapped,
    nextHistory: string[],
    nextC?: string,
  ) => {
    const newQuery = queryString.stringify({
      ...nextFilter,
      c: nextC || undefined,
      ch: nextHistory.length ? nextHistory.join(',') : undefined,
    })
    navigate(`${PageUrls.Orders.Telesale}?${newQuery}`, {
      replace: true,
    })
  }

  const handleSearch = (newFilter: IFilterMapped) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
    setCursor({})
    setCursorHistory([])
    navigateWithCursor(newFilter, [], undefined)
  }

  const handleNext = (nextCursor: string) => {
    if (!nextCursor) return
    const newHistory = [...cursorHistory, nextCursor]
    setCursorHistory(newHistory)
    setCursor({ next: nextCursor })
    navigateWithCursor(filter, newHistory, nextCursor)
  }

  const handlePrev = () => {
    const newHistory = cursorHistory.slice(0, -1)
    setCursorHistory(newHistory)
    const last = newHistory[newHistory.length - 1]
    setCursor(last ? { next: last } : {})
    navigateWithCursor(filter, newHistory, last)
  }

  const handlePerPageChange = (perPage: number) => {
    const next = { ...filter, perPage, page: 1 }
    setFilter(next)
    setSearchFilter(next)
    setCursor({})
    setCursorHistory([])
    navigateWithCursor(next, [], undefined)
  }

  const params = {
    ...searchFilter,
    status: EOrderStatus.TELESALE,
    showTotal: '1',
    statusCall:
      (searchFilter?.statusCall as any) == 'all'
        ? undefined
        : searchFilter.statusCall,
    statusOrderShowTracking:
      (searchFilter?.statusOrderShowTracking as any) == 'all'
        ? undefined
        : searchFilter.statusOrderShowTracking,
    statusShowCrmTelesale:
      (searchFilter?.statusShowCrmTelesale as any) == 'all'
        ? undefined
        : searchFilter.statusShowCrmTelesale,
    dateType:
      (searchFilter?.dateType as any) == 'all'
        ? undefined
        : searchFilter.dateType,
    trackingProvider:
      searchFilter?.trackingProvider === 'all'
        ? undefined
        : searchFilter?.trackingProvider,
    paymentMethod:
      searchFilter?.paymentMethod === 'all'
        ? undefined
        : searchFilter?.paymentMethod,
  }

  const cursorParams = {
    ...params,
    page: undefined,
    nextCursor: cursor.next,
    prevCursor: cursor.prev,
  }

  const ordersList = useAxios<{
    records: IOrder[]
    nextCursor: string | null
    prevCursor: string | null
    hasNext: boolean
    hasPrev: boolean
    total: OrderSummary
  }>({
    method: 'GET',
    url: '/order/v1/cursor',
    params: cursorParams,
  })

  const handleOnDownload = useCallback(
    async (date: Dayjs) => {
      let loop = true
      let page = 1,
        perPage = MAX_DONWLOAD_PAGE_SIZE
      const orders: IOrder[] = []
      let startDate = params?.startDate
      let endDate = params?.endDate

      if (
        !startDate ||
        !endDate ||
        dayjs(endDate).diff(dayjs(startDate), 'day') > 31
      ) {
        startDate = date.startOf('month').format('YYYY-MM-DD')
        endDate = date.endOf('month').format('YYYY-MM-DD')
      }
      while (loop) {
        try {
          const ordersResult = await axiosInstance.get('/order', {
            params: {
              ...params,
              page,
              perPage,
              status: EOrderStatus.TELESALE,
              startDate,
              endDate,
            },
          })
          page++
          if (!ordersResult?.data?.records.length) {
            loop = false
            break
          }
          orders.push(...ordersResult.data.records)
          if (ordersResult?.data?.count < perPage) {
            loop = false
            break
          }
        } catch (e) {
          message.warning('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูลบางส่วน')
          break
        }
      }
      if (!orders.length) {
        message.warning('ไม่พบข้อมูล')
        return
      }
      const rows = orders?.map((order: IOrder) => {
        const isCod = order.paymentMethod === 'COD'
        return {
          สถานะ: OrderSatausLabel?.[order?.status ?? ''] ?? order.status,
          รหัสคำสั่งซื้อ: `${order.code}`,
          หมายเลขพัสดุ: order.trackingCode ?? '-',
          ขนส่ง: SHIPPING_PROVIDER_TEXT?.[order?.trackingProvider ?? ''] ?? '-',
          ชื่อลูกค้า: order.recipient.name,
          เบอร์โทร: order.recipient.phone,
          ที่อยู่: `${order.recipient.address} ${order.recipient.subDistrict} ${order.recipient.district} ${order.recipient.province} ${order.recipient.zipcode}`,
          social: order.recipient?.social || '-',
          จำนวน: order.quantity,
          สินค้า: order.items
            .map((product) => {
              return `${product.productName} (${product.quantity}) ขนาด: ${
                product.meta?.size || '-'
              } สี: ${product.meta?.color || '-'} น้ำหนัก: ${
                product.meta?.weight || '-'
              }
              `
            })
            .join(', '),
          COD: isCod ? order.total : '',
          โอน: isCod ? '' : order.total,
          สร้างคำสั่งซื้อโดย: order.actor,
          รับค่าคอมโดย: order.items
            .map((product) => {
              return `${product.commissionUsername}`
            })
            .join(', '),
          ช่องทางการขาย: order.distribution?.name ?? '-',
          วันที่คำสั่งซื้อ: dayjs(order.orderDate).format('DD/MM/YYYY HH:mm'),
          วันที่จัดส่ง: order.pickUpAt
            ? dayjs(order.pickUpAt).format('DD/MM/YYYY HH:mm')
            : '-',
          หมายเหตุ: order.note,
          วันที่สร้าง: dayjs(order.createdAt).format('DD/MM/YYYY HH:mm'),
          วันที่อัพเดท: dayjs(order.updatedAt).format('DD/MM/YYYY HH:mm'),
        }
      })
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, `order-telesale.xlsx`)
      message.success('ดาวน์โหลดสำเร็จ')
    },
    [params],
  )

  const handleSelectMenuOrder = async (status: EOrderStatus) => {
    try {
      setSelectedOrders([])
      await axiosInstance.put('/order/status', {
        status,
        objectIds: selectedOrders.map((order) => order.objectId),
      })
      onUpdateOrderSuccess()
      message.success('เปลี่ยนสถานะสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const onUpdateOrderSuccess = useCallback(async () => {
    try {
      await ordersList.mutate()
    } catch (error) {}
  }, [filter])

  const handleImportLeadByOrders = async (data: IImportByOrdersForm) => {
    try {
      setSelectedOrders([])
      const leads = await axiosInstance.post<
        {
          status?: LeadExcelStatus
          message?: string
        }[]
      >(`/lead/import-by-orders`, {
        ...data,
        objectIds: selectedOrders.map((order) => order.objectId),
      })

      const ordersStatus = selectedOrders.map((order, index) => ({
        order,
        leadMessage: leads.data[index],
      }))
      // const messageStatus = `นำเข้าทะเบียนลูกค้าสำเร็จ ${ordersStatus.filter(order => order.leadMessage.status === LeadExcelStatus.Success).map(order => order.order.code).join(', ')} รายการ ไม่สำเร็จ ${ordersStatus.filter(order => order.leadMessage.status === LeadExcelStatus.Warning).map(order => `${order.order.code} เนื่องจาก ${order.leadMessage.message}`).join(', ')} รายการ`
      const messageStatus = `นำข้อมูลเข้าแล้ว ${
        ordersStatus.filter(
          (order) => order.leadMessage.status === LeadExcelStatus.Success,
        ).length
      } รายการ ยังไม่หมดอายุ ${
        ordersStatus.filter(
          (order) => order.leadMessage.status === LeadExcelStatus.Warning,
        ).length
      } รายการ`
      if (
        ordersStatus.some(
          (order) => order.leadMessage.status === LeadExcelStatus.Warning,
        )
      ) {
        message.warning(messageStatus)
      } else {
        message.success(messageStatus)
      }
      await ordersList.mutate()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  return (
    <ContentWrapper>
      {/*<AlertMessage severity="error" message="This is an error message" />*/}
      <Helmet title="รายการคำสั่งซื้อ : TeleSale" />
      <Grid container justifyContent="flex-start" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="รายการคำสั่งซื้อ : TeleSale"
            routes={Menus}
            descriptions="รายการคำสั่งซื้อ : TeleSale"
            tags={
              [UserRole.ADMIN, UserRole.MERCHANT].some(
                (role) => (user.roles as UserRole[])?.includes(role),
              ) || merchant.config?.display?.totalOrder
                ? [
                    {
                      title: 'จำนวน',
                      color: 'info',
                      value: ordersList.data?.total?.count ?? 0,
                      classifier: 'รายการ',
                    },
                    {
                      title: 'COD',
                      color: 'warning',
                      value: ordersList.data?.total?.codTotal ?? 0,
                      classifier: 'บาท',
                    },
                    {
                      title: 'โอน',
                      color: 'primary',
                      value: ordersList.data?.total?.bankTotal ?? 0,
                      classifier: 'บาท',
                    },
                    // {
                    //   title: 'อัพเซลสำเร็จ',
                    //   color: 'info',
                    //   value: ordersList.data?.total?.upsaleTotal ?? 0,
                    //   classifier: 'รายการ',
                    // },
                    // {
                    //   title: 'อัพเซลไม่สำเร็จ',
                    //   color: 'error',
                    //   value: ordersList.data?.total?.unsuccessUpSaleTotal ?? 0,
                    //   classifier: 'รายการ',
                    // },
                    // {
                    //   title: 'ยอดขาย TELESALE',
                    //   color: 'success',
                    //   value: ordersList.data?.total?.upsalePriceTotal ?? 0,
                    //   classifier: 'บาท',
                    // },
                  ]
                : undefined
            }
          />
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <OrderFilter
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          onDownload={handleOnDownload}
          showStatusCall={true}
        />
        {selectedOrders.length > 0 && (
          <OrderMenuSelect
            status={EOrderStatus.TELESALE}
            include={true}
            onClick={(value: EOrderStatus) => {
              handleSelectMenuOrder(value)
            }}
            onClickImport={(data: IImportByOrdersForm) => {
              handleImportLeadByOrders(data)
            }}
            total={selectedOrders.length}
          />
        )}
        <OrderListTeleSaleTable
          onUpdateOrderSuccess={onUpdateOrderSuccess}
          isLoading={ordersList.isLoading}
          data={ordersList.data?.records ?? []}
          selectedData={selectedOrders}
          setSelectedData={setSelectedOrders}
          filter={{ ...filter }}
          onFilterChange={handleSearch}
          total={ordersList.data?.total?.count ?? 0}
          hasNext={ordersList.data?.hasNext ?? false}
          hasPrev={cursorHistory.length > 0}
          onNext={() => handleNext(ordersList.data?.nextCursor ?? '')}
          onPrev={handlePrev}
          onPerPageChange={handlePerPageChange}
          pageIndex={cursorHistory.length + 1}
        />
      </Card>
    </ContentWrapper>
  )
}

export default OrderListTeleSalePage
